<template>
    <div
        :class="{ 'slds-show_inline-block': inForm }"
        @mouseenter="enter"
        @mouseleave="leave"
    >
        <div
            v-if="showIcon"
            ref="icon"
            :class="{'slds-form-element__icon': inForm}"
        >
            <slot name="icon">
                <button
                    aria-describedby="help"
                    class="slds-button slds-button_icon"
                >
                    <MAIconInline
                        :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/utility/info.svg')"
                        :iconClass="['slds-icon', 'slds-icon', 'slds-icon_xx-small', 'slds-icon-text-default']"
                        :assistiveText="helpLabel"
                        class="slds-icon_container slds-icon-utility-info"
                    />
                </button>
            </slot>
        </div>

        <div
            ref="popover"
            :class="[nubbin]"
            class="slds-popover slds-popover_tooltip"
            role="tooltip"
        >
            <div
                :class="{'ma-tooltip-nowrap': noWrap}"
                class="slds-popover__body"
            >
                <slot name="content">default content</slot>
            </div>
        </div>
    </div>
</template>

<script>
    import MAIconInline from './ma-icon-inline';

    const nubbins = {
        top: 'slds-nubbin_top',
        topLeft: 'slds-nubbin_top-left',
        topRight: 'slds-nubbin_top-right',
        bottom: 'slds-nubbin_bottom',
        bottomLeft: 'slds-nubbin_bottom-left',
        bottomRight: 'slds-nubbin_bottom-right',
        left: 'slds-nubbin_left',
        leftTop: 'slds-nubbin_left-top',
        leftBottom: 'slds-nubbin_left-bottom',
        right: 'slds-nubbin_right',
        rightTop: 'slds-nubbin_right-top',
        rightBottom: 'slds-nubbin_right-bottom'
    };

    export default {
        name: 'MATooltip',
        components: { MAIconInline },
        description: 'Renders a SLDS-themed tooltip.',
        props: {
            inForm: {
                type: Boolean,
                default: false
            },
            showIcon: {
                type: Boolean,
                default: false
            },
            // Bind the tooltip to the parent node
            bindToParent: {
                type: Boolean,
                default: false
            },
            // Bind to a specific element
            bindTo: {
                type: [HTMLElement, Function],
                default: () => {}
            },
            // The element when is moused over detaches and renders the el
            source: {
                type: [HTMLElement, Function],
                default: () => {}
            },
            // Automatically shows the detached el
            forceShow: {
                type: Boolean,
                default: false
            },
            // The rem set for the nubbin
            nubbinREM: {
                type: Number,
                default: 1.8
            },
            // milliseconds of time before detach/positioning logic is run
            renderDelay: {
                type: Number,
                default: 0
            },
            helpLabel: {
                type: String,
                default: 'Help'
            },
            noWrap: {
                type: Boolean,
                default: false
            },
            nubbinPosition: {
                type: String,
                default: 'bottom'
            }
        },
        data() {
            return {
                showEl: false,
                container: null,
                el: null,
                bindToStored: this.bindTo,
                sourceStored: null,
                nubbin: nubbins.bottom
            };
        },
        computed: {
            doShow() {
                return this.forceShow || this.showEl;
            }
        },
        watch: {
            showEl(value) {
                if (value) {
                    const parent = (this.bindToStored) ? this.bindToStored : this.$refs.icon;
                    const node = this.$refs.popover;

                    if (this.renderDelay) {
                        setTimeout(this.detach.bind(this, parent, node), this.renderDelay);
                    } else {
                        this.detach(parent, node);
                    }
                } else {
                    this.destroy();
                }
            },
            source(value) {
                this.sourceStored = value;
            },
            sourceStored(value) {
                if (this.bindToStored && !value) {
                    throw new Error('If using a custom binding element for MATooltip, a source element must be provided.');
                }

                if (value && !this.bindToStored) {
                    this.bindToStored = value;
                }

                if (value) {
                    value.addEventListener('mouseover', this.enter);
                    value.addEventListener('mouseout', this.leave);
                }

                this.sourceStored = value;
            },
            bindTo(value) {
                this.bindToStored = value;
            }
        },
        mounted() {
            if (this.bindToParent) {
                this.bindToStored = this.$el.parentNode;
            }

            if (this.bindTo) {
                this.bindToStored = (typeof this.bindTo === 'function') ? this.bindTo() : this.bindTo;
            }

            if (this.source) {
                this.sourceStored = (typeof this.source === 'function') ? this.source() : this.source;
            } else {
                this.sourceStored = (this.showIcon) ? this.$refs.icon : this.$el.parentNode;
            }
        },
        beforeDestroy() {
            // destroy the detached element, listeners etc before we destroy the component.
            // if in destroy, artifacts are sometimes left on screen
            this.destroy();

            if (this.sourceStored) {
                this.sourceStored.removeEventListener('mouseover', this.enter);
                this.sourceStored.removeEventListener('mouseout', this.leave);
            }
        },
        methods: {
            enter() {
                this.showEl = true;
            },
            leave(e) {
                const toElement = e.toElement || e.relatedTarget;
                if ((this.sourceStored && this.sourceStored.contains(toElement)) || this.el === toElement || (this.el && this.el.contains(toElement))) {
                    e.stopPropagation();
                    return;
                }

                this.showEl = false;
            },
            overPopup(e) {
                e.stopPropagation();
            },
            outPopup(e) {
                const toElement = e.toElement || e.relatedTarget;
                if ((this.sourceStored && this.sourceStored.contains(toElement)) || this.el === toElement || (this.el && this.el.contains(toElement))) {
                    e.stopPropagation();
                    return;
                }

                this.showEl = false;
            },
            detach(parent, node) {
                this.container = document.createElement('div');
                this.container.className = `slds-scope ${this.$el.className}`;
                this.$el.getAttributeNames().forEach((attr) => {
                    if (attr.startsWith('data-v')) {
                        this.container.setAttribute(attr, this.$el.getAttribute(attr));
                    }
                });

                this.el = node.cloneNode(true);
                this.el.style.display = 'block';
                this.el.addEventListener('mouseleave', this.outPopup);
                this.el.addEventListener('mouseenter', this.overPopup);

                this.container.appendChild(this.el);
                document.body.appendChild(this.container);

                this.setPosition(parent);
            },
            setPosition(parent) {
                const popoverHeight = this.el.scrollHeight;
                const popoverWidth = this.el.clientWidth;

                const nubbinOffset = Math.ceil(this.nubbinREM * parseFloat(getComputedStyle(this.el).fontSize));
                const nubbinWidth = parseFloat(window.getComputedStyle(this.el, ':before').width);
                // eslint-disable-next-line no-restricted-properties
                const nubbinTransformedWidthFloor = Math.floor(Math.sqrt(Math.pow(nubbinWidth, 2) * 2) / 2);
                // eslint-disable-next-line no-restricted-properties
                const nubbinTransformedWidthCeil = Math.ceil(Math.sqrt(Math.pow(nubbinWidth, 2) * 2) / 2);

                const scrollTop = window.pageYOffset;
                const scrollLeft = window.pageXOffset;
                const viewPortWidth = document.documentElement.clientWidth;

                const parentRect = this.getParentRect(parent);
                const parentMiddleX = (parentRect.left + (parentRect.width / 2)) - 1;

                let left = (parentMiddleX - (popoverWidth / 2)) + scrollLeft;
                let top = (parentRect.top - popoverHeight - 1) + scrollTop;

                const currentNubbin = (typeof nubbins[this.nubbinPosition] !== 'undefined') ? nubbins[this.nubbinPosition] : this.nubbin;
                let nubbinAdjusted = this.nubbinPosition;

                // Default vertical overflow
                if (currentNubbin === nubbins.bottom && top - scrollTop < 0) {
                    nubbinAdjusted = 'top';
                }

                // Bottom-right nubbin representation is off right side of page
                if (parentMiddleX + nubbinOffset >= viewPortWidth) {
                    nubbinAdjusted = 'right';

                    // Vertical overflow
                    if (top - scrollTop < 0) {
                        nubbinAdjusted = 'rightTop';
                    }
                // Bottom nubbin representation (default) is off right side of page
                } else if ((parentMiddleX + (popoverWidth / 2) > viewPortWidth) || (this.nubbinPosition === 'bottomRight')) {
                    nubbinAdjusted = 'bottomRight';

                    if (top - scrollTop < 0) {
                        nubbinAdjusted = 'topRight';
                    }
                // Bottom-left nubbin representation is off left side of page
                } else if (parentMiddleX - nubbinOffset <= 0) {
                    nubbinAdjusted = 'left';

                    // Vertical overflow
                    if (top - scrollTop < 0) {
                        nubbinAdjusted = 'leftTop';
                    }
                // Bottom nubbin representation (default) is off left side of page
                } else if (parentMiddleX - (popoverWidth / 2) < 0) {
                    nubbinAdjusted = 'bottomLeft';

                    // Vertical overflow
                    if (top - scrollTop < 0) {
                        nubbinAdjusted = 'topLeft';
                    }
                }

                if (nubbinAdjusted === 'top') {
                    top = parentRect.bottom + scrollTop + nubbinTransformedWidthFloor;
                } else if (nubbinAdjusted === 'topLeft') {
                    top = parentRect.bottom + scrollTop + nubbinTransformedWidthFloor;
                    left = (parentMiddleX - nubbinOffset) + scrollLeft;
                } else if (nubbinAdjusted === 'topRight') {
                    top = parentRect.bottom + scrollTop + nubbinTransformedWidthFloor;
                    left = (parentMiddleX - popoverWidth) + nubbinOffset + scrollLeft;
                } else if (nubbinAdjusted === 'rightTop') {
                    top = (parentRect.top + scrollTop + (parentRect.height / 2)) - nubbinOffset;
                    left = (parentRect.left - popoverWidth - nubbinTransformedWidthCeil) + scrollLeft;
                } else if (nubbinAdjusted === 'leftTop') {
                    top = (parentRect.top + scrollTop + (parentRect.height / 2)) - nubbinOffset;
                    left = parentRect.right + nubbinTransformedWidthFloor + scrollLeft;
                } else if (nubbinAdjusted === 'right') {
                    top = ((parentRect.top + (parentRect.height / 2)) - (this.el.clientHeight / 2)) + scrollTop;
                    left = (parentRect.left - popoverWidth - nubbinTransformedWidthCeil) + scrollLeft;
                } else if (nubbinAdjusted === 'bottomRight') {
                    top = (parentRect.top - popoverHeight - 1) + scrollTop;
                    left = (parentMiddleX - popoverWidth) + nubbinOffset + scrollLeft;
                } else if (nubbinAdjusted === 'left') {
                    left = parentRect.right + nubbinTransformedWidthFloor + scrollLeft;
                    top = ((parentRect.top + ((parentRect.height / 2) - 1)) - (this.el.clientHeight / 2)) + scrollTop;
                } else if (nubbinAdjusted === 'bottomLeft') {
                    left = (parentMiddleX - nubbinOffset) + scrollLeft;
                    top = (parentRect.top - popoverHeight - 1) + scrollTop;
                } else if (nubbinAdjusted === 'bottom') {
                    left = (parentMiddleX - (popoverWidth / 2)) + scrollLeft;
                    top = (parentRect.top - popoverHeight - 1) + scrollTop;
                }

                if (nubbins[nubbinAdjusted] !== this.nubbin) {
                    this.$refs.popover.classList.remove(this.nubbin);
                    this.$refs.popover.classList.add(nubbins[nubbinAdjusted]);
                }

                this.el.style.left = `${left}px`;
                this.el.style.top = `${top}px`;
            },
            // Get parent dimensions including padding, margin, border
            getParentRect(parent) {
                const parentRect = parent.getBoundingClientRect();
                const parentStyle = parent.currentStyle || window.getComputedStyle(parent);

                let parentBufferX = parseFloat(parentStyle.marginLeft) + parseFloat(parentStyle.marginRight);
                parentBufferX += parseFloat(parentStyle.paddingLeft) + parseFloat(parentStyle.paddingRight);
                parentBufferX += parseFloat(parentStyle.borderLeftWidth) + parseFloat(parentStyle.borderRightWidth);

                let parentBufferY = parseFloat(parentStyle.marginTop) + parseFloat(parentStyle.marginBottom);
                parentBufferY += parseFloat(parentStyle.paddingTop) + parseFloat(parentStyle.paddingBottom);
                parentBufferY += parseFloat(parentStyle.borderTopWidth) + parseFloat(parentStyle.borderBottomWidth);

                return {
                    top: parentRect.top - parentBufferY,
                    right: parentRect.right + parentBufferX,
                    bottom: parentRect.bottom + parentBufferY,
                    left: parentRect.left - parentBufferX,
                    width: parentRect.width + parentBufferX,
                    height: parentRect.height + parentBufferY
                };
            },
            destroy() {
                if (this.el) {
                    this.el.removeEventListener('mouseleave', this.outPopup);
                    this.el.removeEventListener('mouseenter', this.overPopup);
                    this.el.remove();
                }

                if (this.container) this.container.remove();

                // Removes event listeners via garbage collector
                this.el = null;
                this.container = null;
                this.nubbin = nubbins.bottom;
            }
        }
    };
</script>

<style scoped>
    .slds-popover_tooltip {
        display: none;
        position: absolute;
        visibility: visible;
        opacity: 1;
        overflow: visible;
        cursor: default;
        z-index: 9999;
    }

    .slds-form-element__icon {
        top: -1px;
        left: 2px;
    }

    .ma-tooltip-nowrap {
        white-space: nowrap;
    }
</style>
